import React from "react";
import { Calendar, ExternalLink, Newspaper, PenLine, Quote } from "lucide-react";
import { Button } from "./ui/button";

const ARTICLES = [
  {
    type: "News",
    typeIcon: Newspaper,
    title: "Greg Clark Announces Candidacy for Oswego County Legislature, 19th District",
    source: "Oswego County Today",
    date: "July 22, 2026",
    excerpt:
      "Town of Oswego resident and veteran municipal finance professional Greg Clark has entered the race for the 19th District seat on the Oswego County Legislature. With over 45 years of experience advising cities and counties on budgets, operations, and economic growth, Clark says he is ready to bring that expertise home to serve local residents. His campaign centers on five priorities: financial responsibility, economic development, public safety, smart government, and protecting taxpayers.",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_bold-davinci-9/artifacts/tip2jseb_ee8cc1_d6aacd052ee1427ab76875446c613073~mv2.webp",
    url: "https://oswegocountytoday.com/community/greg-clark-announces-candidacy-for-oswego-county-legislature-19th-district/",
    tags: ["Announcement", "Campaign"],
    byline: "Published by Step One Creative",
    cta: "Read Full Article",
  },
  {
    type: "Editorial",
    typeIcon: PenLine,
    title: "Blame the Democrats",
    source: "The Palladium Times",
    date: "July 2026",
    isLetter: true,
    body: [
      "To the editor:",
      "Debbie Chambers raises some valid points about Oswego County\u2019s economic base in her letter to the editor of July 8.",
      "Her analysis is flawed though in one important respect: Oswego County is limited in its ability to create an economic climate that will attract new business. Instead, we\u2019re forced to operate under policies set by the Democrats who control Albany and New York City.",
      "I spent 45 years as a financial analyst of counties and other governments throughout the U.S., and one thing was clear. A state\u2019s laws and policies, especially in terms of taxation and economic development, determine the business climate of its local governments.",
      "And which state is ranked dead last in business climate? According to the Tax Foundation, it\u2019s New York State.",
      "If you\u2019re looking for who to blame for upstate New York\u2019s lagging economic growth, look to the Democrats, who have controlled both the governorship and state legislature since 2019.",
      "The last time the Republicans had that same kind of clout was more than 50 years ago, in 1975.",
      "The best way to foster Oswego County\u2019s economic growth is to vote Republican, beginning with Bruce Blakeman, the party\u2019s candidate for governor, and all Republican candidates for town, city and county office.",
    ],
    signature: {
      name: "Gregory A. Clark",
      location: "Oswego",
      note: "Clark is the Republican and Conservative Party candidate for the 19th District of the Oswego County Legislature.",
    },
    tags: ["Letter to the Editor", "Op-Ed"],
    image: null,
    url: null,
  },
];

const NewsCard = ({ a }) => {
  const TypeIcon = a.typeIcon;

  // Letter / Editorial (text-forward) card
  if (a.isLetter) {
    return (
      <article className="lift bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="p-8 md:p-12 lg:p-14">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0a3161] text-white text-[10px] font-bold uppercase tracking-[0.18em]">
              <TypeIcon className="w-3 h-3" />
              {a.type}
            </span>
            {a.tags.map((t) => (
              <span
                key={t}
                className="px-3 py-1 rounded-full bg-slate-100 text-[#0a3161] text-[10px] font-bold uppercase tracking-[0.18em]"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-2 text-[#0a3161] font-semibold uppercase tracking-[0.18em]">
              <Newspaper className="w-3.5 h-3.5" />
              {a.source}
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-500 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              {a.date}
            </span>
          </div>

          <h3 className="mt-4 font-serif-display text-3xl md:text-4xl lg:text-[40px] font-extrabold text-[#0a3161] leading-tight">
            {a.title}
          </h3>

          <div className="mt-6 h-1 w-16 bg-[#b22234] rounded-full" />

          <div className="relative mt-8">
            <Quote className="absolute -top-2 -left-2 w-10 h-10 text-[#b22234]/15" />
            <div className="font-serif-display text-[#1f2a44] text-[17.5px] md:text-[19px] leading-[1.75] space-y-5">
              {a.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="font-serif-display text-lg font-bold text-[#0a3161]">
              {a.signature.name}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">{a.signature.location}</div>
            <p className="text-xs text-slate-500 italic mt-4 leading-relaxed max-w-2xl">
              {a.signature.note}
            </p>
          </div>
        </div>
      </article>
    );
  }

  // Standard news card with image
  return (
    <article className="lift bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-2 relative bg-slate-100 min-h-[280px] lg:min-h-full">
          <img
            src={a.image}
            alt={a.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            {a.tags.map((t) => (
              <span
                key={t}
                className="px-3 py-1 rounded-full bg-[#b22234] text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 p-8 md:p-10 lg:p-12 flex flex-col">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-2 text-[#0a3161] font-semibold uppercase tracking-[0.18em]">
              <TypeIcon className="w-3.5 h-3.5" />
              {a.source}
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-500 font-medium">
              <Calendar className="w-3.5 h-3.5" />
              {a.date}
            </span>
          </div>

          <h3 className="mt-5 font-serif-display text-2xl md:text-3xl lg:text-[32px] font-extrabold text-[#0a3161] leading-tight">
            {a.title}
          </h3>

          <p className="mt-5 text-slate-600 text-[15.5px] leading-relaxed">
            {a.excerpt}
          </p>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between gap-4">
            <span className="text-xs text-slate-500 italic">{a.byline}</span>
            {a.url && (
              <a href={a.url} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#0a3161] hover:bg-[#082448] text-white rounded-full h-11 px-6 text-[14px] font-semibold group">
                  {a.cta || "Read More"}
                  <ExternalLink className="w-4 h-4 ml-2 opacity-80 group-hover:opacity-100 transition-opacity" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const News = () => {
  return (
    <section id="news" className="py-24 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#b22234] font-bold mb-4">
            Campaign Updates
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-extrabold text-[#0a3161]">
            News &amp; Editorials
          </h2>
          <div className="mt-6 h-1 w-20 bg-[#b22234] rounded-full mx-auto" />
        </div>

        <div className="mt-14 max-w-6xl mx-auto space-y-8">
          {ARTICLES.map((a) => (
            <NewsCard key={a.title} a={a} />
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          More campaign updates coming soon &mdash; check back for the latest news.
        </p>
      </div>
    </section>
  );
};

export default News;
